<template>
  <div>
    <topsearch title="Timezone Ayarları" />
    <div class="flex">
      <div class="w-72 border-r h-full">
        <adminSidebar />
      </div>
      <div class="w-full md:p-4">
        <div class="max-w-xl">
          <h2 class="text-lg font-medium mb-4">Sistem Timezone Ayarı</h2>

          <div class="bg-white border rounded-lg p-4">
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Timezone Seçin
              </label>
              <select
                ref="select"
                v-model="selectedTimezone"
                class="uk-select w-full"
              >
                <option value="">Seçiniz</option>
                <optgroup v-if="extraZones.length" label="Listede olmayan">
                  <option v-for="zone in extraZones" :key="zone" :value="zone">
                    {{ zone }} ({{ formatOffset(offsetMinutes(zone)) }})
                  </option>
                </optgroup>
                <optgroup label="Avrupa">
                  <option value="Europe/Istanbul">Europe/Istanbul (UTC+3)</option>
                  <option value="Europe/Warsaw">Europe/Warsaw (UTC+1/+2)</option>
                  <option value="Europe/London">Europe/London (UTC+0/+1)</option>
                  <option value="Europe/Paris">Europe/Paris (UTC+1/+2)</option>
                  <option value="Europe/Berlin">Europe/Berlin (UTC+1/+2)</option>
                  <option value="Europe/Amsterdam">Europe/Amsterdam (UTC+1/+2)</option>
                  <option value="Europe/Rome">Europe/Rome (UTC+1/+2)</option>
                  <option value="Europe/Madrid">Europe/Madrid (UTC+1/+2)</option>
                  <option value="Europe/Athens">Europe/Athens (UTC+2/+3)</option>
                  <option value="Europe/Moscow">Europe/Moscow (UTC+3)</option>
                  <option value="Europe/Kiev">Europe/Kiev (UTC+2/+3)</option>
                  <option value="Europe/Bucharest">Europe/Bucharest (UTC+2/+3)</option>
                  <option value="Europe/Sofia">Europe/Sofia (UTC+2/+3)</option>
                  <option value="Europe/Prague">Europe/Prague (UTC+1/+2)</option>
                  <option value="Europe/Vienna">Europe/Vienna (UTC+1/+2)</option>
                  <option value="Europe/Stockholm">Europe/Stockholm (UTC+1/+2)</option>
                  <option value="Europe/Oslo">Europe/Oslo (UTC+1/+2)</option>
                  <option value="Europe/Helsinki">Europe/Helsinki (UTC+2/+3)</option>
                  <option value="Europe/Dublin">Europe/Dublin (UTC+0/+1)</option>
                  <option value="Europe/Lisbon">Europe/Lisbon (UTC+0/+1)</option>
                  <option value="Europe/Zurich">Europe/Zurich (UTC+1/+2)</option>
                </optgroup>
                <optgroup label="Amerika">
                  <option value="America/New_York">America/New_York (UTC-5/-4)</option>
                  <option value="America/Los_Angeles">America/Los_Angeles (UTC-8/-7)</option>
                  <option value="America/Chicago">America/Chicago (UTC-6/-5)</option>
                  <option value="America/Toronto">America/Toronto (UTC-5/-4)</option>
                  <option value="America/Sao_Paulo">America/Sao_Paulo (UTC-3)</option>
                  <option value="America/Mexico_City">America/Mexico_City (UTC-6/-5)</option>
                </optgroup>
                <optgroup label="Asya">
                  <option value="Asia/Dubai">Asia/Dubai (UTC+4)</option>
                  <option value="Asia/Singapore">Asia/Singapore (UTC+8)</option>
                  <option value="Asia/Tokyo">Asia/Tokyo (UTC+9)</option>
                  <option value="Asia/Hong_Kong">Asia/Hong_Kong (UTC+8)</option>
                  <option value="Asia/Shanghai">Asia/Shanghai (UTC+8)</option>
                  <option value="Asia/Seoul">Asia/Seoul (UTC+9)</option>
                  <option value="Asia/Kolkata">Asia/Kolkata (UTC+5:30)</option>
                  <option value="Asia/Jakarta">Asia/Jakarta (UTC+7)</option>
                  <option value="Asia/Bangkok">Asia/Bangkok (UTC+7)</option>
                  <option value="Asia/Karachi">Asia/Karachi (UTC+5)</option>
                  <option value="Asia/Tehran">Asia/Tehran (UTC+3:30)</option>
                  <option value="Asia/Riyadh">Asia/Riyadh (UTC+3)</option>
                  <option value="Asia/Jerusalem">Asia/Jerusalem (UTC+2/+3)</option>
                </optgroup>
                <optgroup label="Okyanusya">
                  <option value="Australia/Sydney">Australia/Sydney (UTC+10/+11)</option>
                  <option value="Australia/Melbourne">Australia/Melbourne (UTC+10/+11)</option>
                  <option value="Pacific/Auckland">Pacific/Auckland (UTC+12/+13)</option>
                </optgroup>
                <optgroup label="Afrika">
                  <option value="Africa/Cairo">Africa/Cairo (UTC+2)</option>
                  <option value="Africa/Johannesburg">Africa/Johannesburg (UTC+2)</option>
                  <option value="Africa/Lagos">Africa/Lagos (UTC+1)</option>
                </optgroup>
                <optgroup label="UTC">
                  <option value="UTC">UTC (UTC+0)</option>
                </optgroup>
              </select>
            </div>

            <div class="mb-4 p-3 bg-gray-50 rounded-lg text-sm">
              <span class="font-medium">Mevcut Timezone:</span>
              <span class="text-tile-600">{{ currentTimezone || 'Ayarlanmamış' }}</span>
            </div>

            <!-- Tarayıcı karşılaştırması: yanlış seçimi kaydetmeden önce görmek için -->
            <div v-if="browserTimezone" class="mb-4 p-3 border rounded-lg text-sm">
              <div class="flex justify-between">
                <span class="text-gray-600">Tarayıcınızın saat dilimi</span>
                <span>
                  {{ browserTimezone }} · {{ formatOffset(offsetMinutes(browserTimezone)) }} ·
                  {{ clock(browserTimezone) }}
                </span>
              </div>
              <div v-if="selectedTimezone" class="flex justify-between mt-1">
                <span class="text-gray-600">Seçili saat dilimi</span>
                <span>
                  {{ selectedTimezone }} · {{ formatOffset(offsetMinutes(selectedTimezone)) }} ·
                  {{ clock(selectedTimezone) }}
                </span>
              </div>

              <div
                v-if="comparison.level === 'error'"
                class="mt-3 p-2 rounded bg-red-50 border border-red-200 text-red-800"
              >
                Seçili saat dilimi şu an tarayıcınızdan
                <b>{{ comparison.diffLabel }}</b> {{ comparison.direction }}. Ekip sizinle aynı
                saat dilimindeyse hatırlatmalar ve raporlardaki saatler bu kadar kayar.
              </div>
              <div
                v-else-if="comparison.level === 'warn'"
                class="mt-3 p-2 rounded bg-yellow-50 border border-yellow-200 text-yellow-800"
              >
                Şu an aynı saati gösteriyor, ancak yaz/kış saati geçişinde
                <b>{{ comparison.diffLabel }}</b> fark oluşacak (ocak: {{ comparison.winter }},
                temmuz: {{ comparison.summer }}).
              </div>
              <div
                v-else-if="comparison.level === 'ok'"
                class="mt-3 p-2 rounded bg-green-50 border border-green-200 text-green-800"
              >
                Seçili saat dilimi tarayıcınızla yıl boyunca aynı saati gösteriyor.
              </div>

              <button
                v-if="browserTimezone !== selectedTimezone"
                type="button"
                class="mt-2 underline text-gray-700"
                @click="selectedTimezone = browserTimezone"
              >
                Tarayıcımın saat dilimini seç ({{ browserTimezone }})
              </button>
              <div class="mt-2 text-xs text-gray-500">
                Bu bilgi sizin bilgisayarınızın ayarından okunur; ekibiniz farklı bir
                ülkedeyse onların saat dilimini seçin.
              </div>
            </div>

            <div class="flex space-x-2">
              <button
                @click="saveTimezone()"
                :disabled="saving || !selectedTimezone"
                class="green-button"
              >
                <span v-if="saving">Kaydediliyor...</span>
                <span v-else>Kaydet</span>
              </button>
            </div>

            <div v-if="message" class="mt-4 p-3 rounded-lg" :class="messageClass">
              {{ message }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SidebarVue from "../components/Ui/Admin/Sidebar.vue";
import http from "../services/http";

function detectBrowserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch (e) {
    return "";
  }
}

export default {
  name: "AdminTimezone",
  components: {
    adminSidebar: SidebarVue
  },
  data() {
    return {
      selectedTimezone: "",
      currentTimezone: "",
      saving: false,
      message: "",
      messageType: "success",
      browserTimezone: detectBrowserTimezone(),
      // Select'te bulunan seçenekler (mounted'da okunur); listede olmayan
      // tarayıcı / kayıtlı saat dilimi ayrıca eklenir.
      listedZones: [],
      // Saat göstergesi dakikada bir tazelenir.
      now: Date.now(),
      clockTimer: null
    };
  },
  computed: {
    extraZones() {
      if (!this.listedZones.length) return [];
      return [this.browserTimezone, this.currentTimezone].filter(
        (zone, i, all) =>
          zone &&
          all.indexOf(zone) === i &&
          this.listedZones.indexOf(zone) === -1 &&
          this.offsetMinutes(zone) !== null
      );
    },
    // İsim değil ofset karşılaştırılır (Europe/Istanbul = Asia/Istanbul).
    // Bugün + ocak + temmuz: yaz saati farkı olan bölgeler de yakalanır.
    comparison() {
      const browser = this.browserTimezone;
      const selected = this.selectedTimezone;
      if (!browser || !selected) return { level: null };
      const year = new Date(this.now).getFullYear();
      const at = (zone, date) => this.offsetMinutes(zone, date);
      // Geçersiz bölgede null döner; null - sayı NaN değil sayı verdiği için ayrıca bakılır.
      if (at(selected) === null || at(browser) === null) return { level: null };
      const nowDiff = at(selected) - at(browser);
      const winter = new Date(Date.UTC(year, 0, 15, 12));
      const summer = new Date(Date.UTC(year, 6, 15, 12));
      const winterDiff = at(selected, winter) - at(browser, winter);
      const summerDiff = at(selected, summer) - at(browser, summer);
      if (nowDiff !== 0) {
        return {
          level: "error",
          diffLabel: this.durationLabel(nowDiff),
          direction: nowDiff > 0 ? "ileride" : "geride"
        };
      }
      if (winterDiff !== 0 || summerDiff !== 0) {
        const worst = Math.abs(winterDiff) > Math.abs(summerDiff) ? winterDiff : summerDiff;
        return {
          level: "warn",
          diffLabel: this.durationLabel(worst),
          winter: this.signedLabel(winterDiff),
          summer: this.signedLabel(summerDiff)
        };
      }
      return { level: "ok" };
    },
    messageClass() {
      return this.messageType === "success"
        ? "bg-green-100 text-green-700"
        : "bg-red-100 text-red-700";
    }
  },
  created() {
    this.loadCurrentTimezone();
  },
  mounted() {
    if (this.$refs.select) {
      this.listedZones = Array.from(this.$refs.select.options).map(o => o.value);
    }
    this.clockTimer = setInterval(() => {
      this.now = Date.now();
    }, 60000);
  },
  beforeDestroy() {
    clearInterval(this.clockTimer);
  },
  methods: {
    // Saat diliminin verilen andaki UTC farkı (dakika); geçersizse null.
    offsetMinutes(zone, date) {
      const at = date || new Date(this.now);
      try {
        const parts = {};
        new Intl.DateTimeFormat("en-US", {
          timeZone: zone,
          hourCycle: "h23",
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit"
        })
          .formatToParts(at)
          .forEach(p => {
            parts[p.type] = p.value;
          });
        const wall = Date.UTC(
          +parts.year,
          +parts.month - 1,
          +parts.day,
          +parts.hour,
          +parts.minute,
          +parts.second
        );
        return Math.round((wall - Math.floor(at.getTime() / 1000) * 1000) / 60000);
      } catch (e) {
        return null;
      }
    },
    formatOffset(minutes) {
      if (minutes === null) return "?";
      const sign = minutes < 0 ? "-" : "+";
      const abs = Math.abs(minutes);
      const m = abs % 60;
      return "UTC" + sign + Math.floor(abs / 60) + (m ? ":" + String(m).padStart(2, "0") : "");
    },
    durationLabel(minutes) {
      const abs = Math.abs(minutes);
      const h = Math.floor(abs / 60);
      const m = abs % 60;
      return (h ? h + " saat" : "") + (h && m ? " " : "") + (m ? m + " dakika" : "");
    },
    signedLabel(minutes) {
      if (!minutes) return "aynı";
      return (minutes > 0 ? "+" : "-") + this.durationLabel(minutes);
    },
    clock(zone) {
      try {
        return new Intl.DateTimeFormat("tr-TR", {
          timeZone: zone,
          hour: "2-digit",
          minute: "2-digit"
        }).format(new Date(this.now));
      } catch (e) {
        return "?";
      }
    },
    async loadCurrentTimezone() {
      try {
        const response = await http.getTimezone();
        if (response.data && response.data.timezone) {
          this.currentTimezone = response.data.timezone;
          this.selectedTimezone = response.data.timezone;
        }
      } catch (error) {
        console.error("Timezone yüklenemedi:", error);
      }
    },
    async saveTimezone() {
      if (!this.selectedTimezone) return;

      this.saving = true;
      this.message = "";

      try {
        const response = await http.saveTimezone(this.selectedTimezone);
        if (response.data && response.data.success) {
          this.currentTimezone = this.selectedTimezone;
          this.message = "Timezone başarıyla kaydedildi.";
          this.messageType = "success";
        } else {
          this.message = response.data.message || "Kaydetme başarısız.";
          this.messageType = "error";
        }
      } catch (error) {
        this.message = "Bir hata oluştu.";
        this.messageType = "error";
        console.error("Timezone kaydetme hatası:", error);
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>
